import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '@/components/ui/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, logout } from '@/api/auth';
import { motion } from 'framer-motion';
import { 
  Settings, 
  MessageCircle,
  Bell,
  Mail,
  Globe,
  Play,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { listMyDiscordPosts } from '@/api/content';
import MainCampusOnly, { isCorrespondenceUser } from '@/components/common/MainCampusOnly';

export default function MyPage() {
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('posts');

  const displayName = user?.display_name || user?.global_name || user?.full_name || user?.discord_username || user?.username || 'User';
  const discordAvatar = user?.avatar_url || user?.discord_avatar;
  const discordTag = user?.discord_tag || user?.username || displayName;
  const isCorrespondenceStudent = isCorrespondenceUser(user);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const userData = await getCurrentUser();
      if (isMounted && userData) {
        setUser(userData);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const {
    data: discordPosts = [],
    isLoading: discordPostsLoading,
    error: discordPostsError,
  } = useQuery({
    queryKey: ['my-discord-posts', user?.id],
    queryFn: () => listMyDiscordPosts({ limit: 30 }),
    enabled: !!user?.id && !isCorrespondenceStudent,
  });

  const { data: settings, refetch: refetchSettings } = useQuery({
    queryKey: ['settings', user?.id],
    queryFn: async () => {
      return null;
    },
    enabled: !!user?.id
  });

  const { data: learningTime = [] } = useQuery({
    queryKey: ['learning-time', user?.id],
    queryFn: () => {
      return [];
    },
    enabled: !!user?.id
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (data) => {
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings', user?.id]);
      refetchSettings();
    }
  });

  const formatDiscordPostDate = (value) => {
    if (!value) return '日時不明';
    const rawValue = String(value).trim();
    const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
    const normalizedValue = rawValue.includes('T') ? rawValue : rawValue.replace(' ', 'T');
    const date = new Date(hasTimezone ? normalizedValue : `${normalizedValue}Z`);
    if (Number.isNaN(date.getTime())) return '日時不明';

    const parts = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${values.year}/${values.month}/${values.day} ${values.hour}:${values.minute}`;
  };

  const getApiErrorDetail = (error) => {
    const status = error?.response?.status ?? error?.message ?? null;
    const code = error?.response?.data?.code;
    const detail = error?.response?.data?.detail;
    return [status, code, detail].filter(Boolean).join(' / ');
  };

  const isImageAttachmentUrl = (value) => {
    if (!value) return false;

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.avif'];
    try {
      const url = new URL(value);
      const pathname = url.pathname.toLowerCase();
      const format = url.searchParams.get('format')?.toLowerCase();

      return (
        imageExtensions.some((extension) => pathname.endsWith(extension)) ||
        ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif'].includes(format)
      );
    } catch {
      const path = String(value).split('?')[0].toLowerCase();
      return imageExtensions.some((extension) => path.endsWith(extension));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-6 mb-8"
        >
          <Avatar className="w-20 h-20">
            {discordAvatar ? (
              <AvatarImage src={discordAvatar} />
            ) : (
              <AvatarFallback className="bg-[#8b6f47] text-[#f5ede3] text-2xl">
                {displayName?.[0] || 'U'}
              </AvatarFallback>
            )}
          </Avatar>
          
          <div>
            <h1 className="text-2xl font-bold text-[#2d1810]">{displayName}</h1>
            <p className="text-[#6b5540]">{discordTag}</p>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1">
            <TabsTrigger value="posts" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8b6f47] data-[state=active]:to-[#6b5540] data-[state=active]:text-[#f5ede3] text-[#5d4529]">
              <MessageCircle className="w-4 h-4 mr-2" />
              投稿一覧
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#8b6f47] data-[state=active]:to-[#6b5540] data-[state=active]:text-[#f5ede3] text-[#5d4529]">
              <Settings className="w-4 h-4 mr-2" />
              {t('settings')}
            </TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-6">
            <MainCampusOnly locked={isCorrespondenceStudent}>
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#2d1810]">
                    <MessageCircle className="w-5 h-5 text-[#8b6f47]" />
                    自分の投稿一覧
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {discordPostsError ? (
                    <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                      投稿一覧の取得に失敗しました: {getApiErrorDetail(discordPostsError)}
                    </div>
                  ) : discordPostsLoading ? (
                    <div className="rounded-xl bg-[#f5ede3]/60 px-4 py-6 text-sm text-[#6b5540]">
                      投稿を読み込み中です。
                    </div>
                  ) : discordPosts.length === 0 ? (
                    <div className="rounded-xl bg-[#f5ede3]/60 px-4 py-6 text-sm text-[#6b5540]">
                      まだ記録されている投稿はありません。
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {discordPosts.map((post, index) => (
                        <div
                          key={`${post.id || 'post'}-${index}`}
                          className="rounded-2xl border border-[#e8dccd] bg-[#faf6f1] p-4"
                        >
                          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-[#6b5540]">
                            {post.channelName && (
                              <Badge className="bg-[#8b6f47]/10 text-[#5d4529] border-[#d4c4b0]">
                                #{post.channelName}
                              </Badge>
                            )}
                            <span>{formatDiscordPostDate(post.postedAt)}</span>
                          </div>
                          {post.content ? (
                            <p className="whitespace-pre-wrap break-words text-sm leading-6 text-[#2d1810]">
                              {post.content}
                            </p>
                          ) : (
                            <p className="text-sm text-[#8a775f]">本文なしの投稿です。</p>
                          )}
                          {post.attachmentUrl && (
                            <div className="mt-3 space-y-2">
                              {isImageAttachmentUrl(post.attachmentUrl) && (
                                <a
                                  href={post.attachmentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block overflow-hidden rounded-xl border border-[#e8dccd] bg-white"
                                >
                                  <img
                                    src={post.attachmentUrl}
                                    alt="投稿の添付画像"
                                    loading="lazy"
                                    className="max-h-96 w-full object-contain"
                                  />
                                </a>
                              )}
                              <a
                                href={post.attachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-medium text-[#6b5540] hover:text-[#2d1810]"
                              >
                                添付ファイルを開く
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          )}
                          {post.jumpUrl && (
                            <div className="mt-3">
                              <a
                                href={post.jumpUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 rounded-lg border border-[#d4c4b0] bg-white px-3 py-2 text-xs font-semibold text-[#5d4529] hover:bg-[#f5ede3]"
                              >
                                Discordで見る
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </MainCampusOnly>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-[#2d1810]">{t('settings')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Language */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#8b6f47]" />
                    <span className="text-[#2d1810]">UI言語</span>
                  </div>
                  <Select value={language} onValueChange={changeLanguage}>
                    <SelectTrigger className="w-32 bg-[#f5ede3]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ja">日本語</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Default Subtitle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-[#8b6f47]" />
                    <span className="text-[#2d1810]">{t('defaultSubtitleLang')}</span>
                  </div>
                  <Select 
                    value={settings?.default_subtitle_language || 'ja'}
                    onValueChange={(v) => updateSettingsMutation.mutate({ default_subtitle_language: v })}
                  >
                    <SelectTrigger className="w-32 bg-[#f5ede3]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">オフ</SelectItem>
                      <SelectItem value="ja">日本語</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Auto Play */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Play className="w-5 h-5 text-[#8b6f47]" />
                    <span className="text-[#2d1810]">{t('autoPlaySetting')}</span>
                  </div>
                  <Switch
                    checked={settings?.auto_play ?? true}
                    onCheckedChange={(v) => updateSettingsMutation.mutate({ auto_play: v })}
                    className="data-[state=checked]:bg-[#8b6f47]"
                  />
                </div>

                {/* Email Notifications */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#8b6f47]" />
                    <span className="text-[#2d1810]">{t('emailNotifications')}</span>
                  </div>
                  <Switch
                    checked={settings?.email_notifications ?? true}
                    onCheckedChange={(v) => updateSettingsMutation.mutate({ email_notifications: v })}
                    className="data-[state=checked]:bg-[#8b6f47]"
                  />
                </div>

                {/* Discord Notifications */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-[#8b6f47]" />
                    <span className="text-[#2d1810]">{t('discordNotifications')}</span>
                  </div>
                  <Switch
                    checked={settings?.discord_notifications ?? true}
                    onCheckedChange={(v) => updateSettingsMutation.mutate({ discord_notifications: v })}
                    className="data-[state=checked]:bg-[#8b6f47]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Logout */}
            <Button 
              variant="outline" 
              className="w-full text-[#5d4529] hover:text-[#2d1810] hover:bg-[#f5ede3]"
              onClick={() => {
                logout();
                navigate('/');
              }}
            >
              {t('logout')}
            </Button>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}